import type { AttributeFieldValue, AttributeOption, ReportFormState } from 'types/tpo/reportSetup';

export const sectionColumnsClass: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
};

export const PAGE_LIMIT = 50;

/**
 * Converts any form of a stored attribute field value into the canonical { all, values } shape.
 * This is needed because a value can arrive in different forms depending on where it came from:
 *   - { all, values }  → already canonical; re-validate and return as-is.
 *   - plain array      → legacy inclusion list; wrap into { all: false, values }.
 *   - null / undefined → nothing selected; return an empty inclusion.
 *   - scalar           → single-select value; wrap into a one-element values array.
 */
export const toAttributeFieldValue = (raw: any): AttributeFieldValue => {
    // Already in the canonical shape — re-validate defensively.
    if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'all' in raw) {
        return { all: !!raw.all, values: Array.isArray(raw.values) ? raw.values : [] };
    }
    // Legacy flat array (pre-inclusion/exclusion model).
    if (Array.isArray(raw)) return { all: false, values: raw };
    // Nothing selected.
    if (raw === null || raw === undefined) return { all: false, values: [] };
    // Single scalar value (e.g. single-select field storing one id directly).
    return { all: false, values: [raw] };
};

/**
 * Extracts the single selected value from a single-select attribute field.
 * Returns undefined when nothing is selected.
 */
export const getAttributeScalar = (raw: any): string | number | undefined => toAttributeFieldValue(raw).values[0];

/**
 * Ensures a boolean attributeExId is stored as a string ("true"/"false") so it consistently
 * matches the stringified form that comes back from the API after a report is saved.
 * Non-boolean values are returned unchanged.
 */
export const normalizeAttributeExId = (exId: any) => (typeof exId === 'boolean' ? String(exId) : exId);

/** Returns a renderable string label for an attribute option. Handles boolean API values and
 *  the `is_tpm_plan` special case (true → TPM, false → TPO). */
export const formatAttributeOptionLabel = (
    desc: any,
    fallback?: string | number | boolean,
    attributeType?: string,
): string => {
    if (attributeType === 'is_tpm_plan') {
        const value = desc ?? fallback;
        return value === true || value === 'true' ? 'TPM' : 'TPO';
    }
    const label = desc === null || desc === undefined || desc === '' ? fallback : desc;
    return label === null || label === undefined ? '' : String(label);
};

/**
 * Recomputes the exclusion list when a multi-select field is in "Select All" mode.
 * Diffs previousSelection vs nextSelection (both are on-screen values reported by antd):
 *   - newly unchecked  → add to exclusion
 *   - newly re-checked → remove from exclusion
 * Values are compared as strings to handle mixed types (string, number, boolean).
 */
export const computeNextExclusion = (
    currentExclusion: (string | number)[],
    previousSelection: (string | number)[],
    nextSelection: (string | number)[],
): (string | number)[] => {
    const toKey = (value: string | number) => String(value);
    const nextSelectionKeys = new Set(nextSelection.map(toKey));
    const previousSelectionKeys = new Set(previousSelection.map(toKey));

    // User unchecked these on screen, so they become newly excluded.
    const newlyExcluded = previousSelection.filter((value) => !nextSelectionKeys.has(toKey(value)));
    // User re-checked these on screen, so they must be dropped from the exclusion list.
    const reincludedKeys = new Set(
        nextSelection.filter((value) => !previousSelectionKeys.has(toKey(value))).map(toKey),
    );

    // Keep existing exclusions except anything the user just re-included...
    const updatedExclusion = currentExclusion.filter((value) => !reincludedKeys.has(toKey(value)));
    // ...then append the newly unchecked values, skipping duplicates.
    const updatedExclusionKeys = new Set(updatedExclusion.map(toKey));
    for (const value of newlyExcluded) {
        if (!updatedExclusionKeys.has(toKey(value))) {
            updatedExclusion.push(value);
            updatedExclusionKeys.add(toKey(value));
        }
    }
    return updatedExclusion;
};

export const isValueFilled = (val: any) => {
    if (val === null || val === undefined) return false;
    if (typeof val === 'string') return val.trim() !== '';
    if (Array.isArray(val)) return val.length > 0;
    // attribute field value: filled if "all" mode is on or there are explicit values
    if (typeof val === 'object' && 'all' in val) {
        return !!val.all || (Array.isArray(val.values) && val.values.length > 0);
    }
    return true;
};

export const REPORT_DETAILS_TO_FORM_KEY_MAP: Record<string, string> = {
    name: 'report_name',
    customer_hierarchy: 'customer_hierarchy',
    product_hierarchy: 'product_hierarchy',
    promotion_granularity: 'promotion_granularity',
    is_plan_level: 'enable_plan_level_data',
};

/** Hydrates the report setup modal form from a saved report returned by the API. */
export const buildFormStateFromReport = (reportDetails: any, firstTabKey: string): ReportFormState => {
    const reportType = reportDetails?.report_type ?? firstTabKey;
    const formFieldValues: Record<string, any> = {};
    // Preset options let saved chips render immediately without waiting for a fetch.
    const presetOptionsByField: Record<string, AttributeOption[]> = {};

    // Flat scalar fields (report_name, customer_hierarchy, etc.) live directly on the report object.
    for (const [reportKey, formKey] of Object.entries(REPORT_DETAILS_TO_FORM_KEY_MAP)) {
        if (reportDetails?.[reportKey] != null) formFieldValues[formKey] = reportDetails[reportKey];
    }

    // Attribute filter fields live inside filters[dim].inclusion / filters[dim].exclusion.
    // Collect them into per-field buckets and build the preset options map at the same time.
    const filters = reportDetails?.filters ?? {};
    const inclusionsByField: Record<string, (string | number)[]> = {};
    const exclusionsByField: Record<string, (string | number)[]> = {};

    const collectFilterItems = (items: any[], targetBucket: Record<string, (string | number)[]>) => {
        for (const item of items) {
            const fieldKey = item?.attributeType;
            if (!fieldKey) continue;
            // Boolean exIds (e.g. is_tpm_plan) are stringified to stay consistent with the
            // "true"/"false" strings the API sends back after saving.
            const exId = normalizeAttributeExId(item.attributeExId);
            (targetBucket[fieldKey] ??= []).push(exId);
            (presetOptionsByField[fieldKey] ??= []).push({
                label: formatAttributeOptionLabel(item.attributeDesc, item.attributeExId, item.attributeType),
                value: exId,
                attributeType: item.attributeType,
                dimension: item.dimension,
                attributeConfigId: item.attributeConfigId,
            });
        }
    };

    for (const dimensionEntry of Object.values(filters) as any[]) {
        collectFilterItems(Array.isArray(dimensionEntry?.inclusion) ? dimensionEntry.inclusion : [], inclusionsByField);
        collectFilterItems(Array.isArray(dimensionEntry?.exclusion) ? dimensionEntry.exclusion : [], exclusionsByField);
    }

    // A saved exclusion means the user had clicked "Select All" then removed a few items.
    // Restore that as { all: true, values: [excluded] }; plain picks restore as { all: false, values: [included] }.
    const allFieldKeys = new Set([...Object.keys(inclusionsByField), ...Object.keys(exclusionsByField)]);
    for (const fieldKey of allFieldKeys) {
        const savedExclusions = exclusionsByField[fieldKey] ?? [];
        const savedInclusions = inclusionsByField[fieldKey] ?? [];
        formFieldValues[fieldKey] = savedExclusions.length
            ? { all: true, values: savedExclusions }
            : { all: false, values: savedInclusions };
    }

    return {
        activeTabKey: reportType,
        formValuesByTab: { [reportType]: formFieldValues },
        presetOptionsByFieldKey: presetOptionsByField,
    };
};
